import React from 'react';

import { bulletList, bulletListWithLocalId } from '@atlaskit/adf-schema/bullet-list';
import { listItem, listItemWithLocalId } from '@atlaskit/adf-schema/list-item';
import {
	orderedListWithOrder,
	orderedListWithOrderAndLocalId,
} from '@atlaskit/adf-schema/ordered-list';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics';
import { toggleBulletList, toggleOrderedList, tooltip } from '@atlaskit/editor-common/keymaps';
import { listMessages as messages } from '@atlaskit/editor-common/messages';
import { IconList, IconListNumber } from '@atlaskit/editor-common/quick-insert';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

import type { ListPlugin } from './listPluginType';
import {
	toggleBulletList as toggleBulletListCommand,
	toggleOrderedList as toggleOrderedListCommand,
} from './pm-plugins/commands';
import { indentList } from './pm-plugins/commands/indent-list';
import { outdentList } from './pm-plugins/commands/outdent-list';
import inputRulePlugin from './pm-plugins/input-rules';
// Ignored via go/ees005
// eslint-disable-next-line import/no-named-as-default
import keymapPlugin from './pm-plugins/keymap';
import { createPlugin, pluginKey as listPluginKey } from './pm-plugins/main';
import { findRootParentListNode } from './pm-plugins/utils/find';
import { isInsideListItem } from './pm-plugins/utils/selection';
import { getListComponents } from './ui';
import { getListQuickInsertComponents } from './ui/quick-insert/getListQuickInsertComponents';

/*
  Toolbar buttons to bullet and ordered list can be found in
  packages/editor/editor-core/src/plugins/toolbar-lists-indentation/ui/Toolbar.tsx
 */

/**
 * List plugin to be added to an `EditorPresetBuilder` and used with `ComposableEditor`
 * from `@atlaskit/editor-core`.
 */
export const listPlugin: ListPlugin = ({ api }) => {
	const featureFlags = api?.featureFlags?.sharedState.currentState() || {};
	const editorAnalyticsAPI = api?.analytics?.actions;

	if (editorExperiment('platform_editor_block_menu', true)) {
		api?.blockMenu?.actions.registerBlockMenuComponents(getListComponents(api));
	}
	const isRegisteredSlashCommandEnabled = isExperimentEnabled('platform_editor_slash_command');
	if (isRegisteredSlashCommandEnabled) {
		api?.uiControlRegistry?.actions.register(getListQuickInsertComponents({ api }));
	}

	return {
		name: 'list',
		actions: {
			isInsideListItem,
			findRootParentListNode,
		},
		commands: {
			indentList: indentList(editorAnalyticsAPI),
			outdentList: (inputMethod) => outdentList(editorAnalyticsAPI)(inputMethod),
			toggleOrderedList: toggleOrderedListCommand(editorAnalyticsAPI),
			toggleBulletList: toggleBulletListCommand(editorAnalyticsAPI),
		},
		getSharedState: (editorState) => {
			if (!editorState) {
				return undefined;
			}

			return listPluginKey.getState(editorState);
		},

		nodes() {
			const getListItemNode = () => {
				if (fg('platform_editor_adf_with_localid')) {
					return listItemWithLocalId;
				}
				return listItem;
			};

			return [
				{
					name: 'bulletList',
					node: fg('platform_editor_adf_with_localid') ? bulletListWithLocalId : bulletList,
				},
				{
					name: 'orderedList',
					node: fg('platform_editor_adf_with_localid')
						? orderedListWithOrderAndLocalId
						: orderedListWithOrder,
				},
				{
					name: 'listItem',
					node: getListItemNode(),
				},
			];
		},

		pmPlugins() {
			return [
				{
					name: 'list',
					plugin: ({ dispatch }) => createPlugin(dispatch, featureFlags, api),
				},
				{
					name: 'listInputRule',
					plugin: ({ schema }) => inputRulePlugin(schema, api?.analytics?.actions),
				},
				{
					name: 'listKeymap',
					plugin: () => keymapPlugin(featureFlags, api?.analytics?.actions),
				},
			];
		},

		pluginsOptions: isRegisteredSlashCommandEnabled
			? {}
			: {
					quickInsert: ({ formatMessage }) => {
						return [
							{
								id: 'unorderedList',
								title: formatMessage(messages.unorderedList),
								description: formatMessage(messages.unorderedListDescription),
								keywords: ['ul', 'unordered'],
								priority: 1100,
								keyshortcut: tooltip(toggleBulletList),
								icon: () => <IconList />,
								action(insert, state) {
									const tr = insert(
										state.schema.nodes.bulletList.createChecked(
											{},
											state.schema.nodes.listItem.createChecked(
												{},
												state.schema.nodes.paragraph.createChecked(),
											),
										),
									);
									editorAnalyticsAPI?.attachAnalyticsEvent({
										action: ACTION.INSERTED,
										actionSubject: ACTION_SUBJECT.LIST,
										actionSubjectId: ACTION_SUBJECT_ID.FORMAT_LIST_BULLET,
										eventType: EVENT_TYPE.TRACK,
										attributes: {
											inputMethod: INPUT_METHOD.QUICK_INSERT,
										},
									})(tr);
									return tr;
								},
							},
							{
								id: 'orderedList',
								title: formatMessage(messages.orderedList),
								description: formatMessage(messages.orderedListDescription),
								keywords: ['ol', 'ordered'],
								priority: 1200,
								keyshortcut: tooltip(toggleOrderedList),
								icon: () => <IconListNumber />,
								action(insert, state) {
									const tr = insert(
										state.schema.nodes.orderedList.createChecked(
											{},
											state.schema.nodes.listItem.createChecked(
												{},
												state.schema.nodes.paragraph.createChecked(),
											),
										),
									);
									editorAnalyticsAPI?.attachAnalyticsEvent({
										action: ACTION.INSERTED,
										actionSubject: ACTION_SUBJECT.LIST,
										actionSubjectId: ACTION_SUBJECT_ID.FORMAT_LIST_NUMBER,
										eventType: EVENT_TYPE.TRACK,
										attributes: {
											inputMethod: INPUT_METHOD.QUICK_INSERT,
										},
									})(tr);
									return tr;
								},
							},
						];
					},
				},
	};
};
